<template>
  <PortalPageShell
    :title="$t('menu.flightmanager')"
    :subtitle="$t('flightManager.subtitle')"
  >
    <div class="portal-page-fill flight-manager">
    <div class="top-header toolbar-card">
      <div class="search" :class="{ 'search--disabled': !loaded }">
        <div class="icon">
          <span class="mdi mdi-magnify"></span>
        </div>
        <input
          :placeholder="$t('search.placeholder')"
          v-model="searchInput"
          :disabled="!loaded"
        />
      </div>
      <div class="filters-container">
        <Tabs :value="filter" @update="updatefilter"/>
      </div>
    </div>
    <div class="list-view table-card">
      <div class="table-scroll">
        <RowActiveFlightHeader :direction="direction" :sort="sort" @updateSort="updateSort" />
        <div class="row-container">
          <template v-if="loaded">
            <RowActiveFlight
              v-for="flight in displayFlights"
              :flight="flight"
              :key="flight.id + '-' + airlineLogosReady"
              :data-id="flight.id"
              @toggleEditActiveFlyout="toggleEditActiveFlyout"
            />
            <div v-if="displayFlights.length === 0" class="no-data-message">
              <div class="message">{{ $t('airportTable.message') }}</div>
            </div>
          </template>
          <FlightTableSkeleton v-else />
        </div>
      </div>
    </div>
    <div class="footer portal-footer">
      <div class="results">
        <span class="mdi mdi-format-list-bulleted results-icon" aria-hidden="true"></span>
        {{ $t('displayresults.display') }} <strong>{{ resultsCopy }}</strong> {{ $t('displayresults.results') }}
      </div>
    </div>
    <FlyoutEditActiveFlight
      v-if="editibleFlight"
      :key="editibleFlight && editibleFlight.id"
      :flight="editibleFlight"
      @toggleEditActiveFlyout="toggleEditActiveFlyout"
    />
    </div>
  </PortalPageShell>
</template>

<script>

import debounce from 'lodash.debounce';
import PortalPageShell from '../components/layout/PortalPageShell.vue';
import RowActiveFlightHeader from '../components/RowActiveFlightHeader.vue';
import RowActiveFlight from '../components/RowActiveFlight.vue';
import FlightTableSkeleton from '../components/FlightTableSkeleton.vue';
import Tabs from '../components/Tabs.vue';
import FlyoutEditActiveFlight from '../components/FlyoutEditActiveFlight.vue';
import ActiveFlights from '../js/services/ActiveFlights';
import AirportSettings from '../js/services/AirportSettings';
import { flightsSampleData } from '../js/flights-sample-data';
import {
  partitionFlightsByType,
  buildSortMetaMap,
  getPartitionList,
  searchFlights,
  sortFlightsFast,
} from '../js/utils/flightSort';
import { mapGetters } from 'vuex';
import { preloadAirlineLogos } from '../js/utils/airlineLogo';

export default {
  name: 'FlightManager',
  components: {
    PortalPageShell,
    RowActiveFlightHeader,
    RowActiveFlight,
    FlightTableSkeleton,
    Tabs,
    FlyoutEditActiveFlight,
  },
  computed: {
    ...mapGetters({
      flights: 'activeFlights/flights',
    }),
    resultsCopy() {
      return `${this.displayFlights.length}`;
    }
  },
  created() {
    this.debouncedSetSearchQuery = debounce((value) => {
      this.searchQuery = value;
    }, 250);
    this.rebuildFlightIndex();
    this.updateDisplayFlights();
  },
  beforeDestroy() {
    if (this.debouncedSetSearchQuery && this.debouncedSetSearchQuery.cancel) {
      this.debouncedSetSearchQuery.cancel();
    }
  },
  watch: {
    searchInput(val) {
      this.debouncedSetSearchQuery(val);
    },
    flights: {
      handler() {
        this.rebuildFlightIndex();
        this.sortCache = {};
        this.updateDisplayFlights();
      },
      deep: true,
    },
    filter() {
      this.updateDisplayFlights();
    },
    searchQuery() {
      this.updateDisplayFlights();
    },
    sort() {
      this.sortCache = {};
      this.warmSortCache();
      this.updateDisplayFlights();
    },
    direction() {
      this.sortCache = {};
      this.warmSortCache();
      this.updateDisplayFlights();
    },
  },
  mounted() {
    this.getAllAirportSettings();
    preloadAirlineLogos().then(() => {
      this.airlineLogosReady = true;
    });
    if (this.flights && this.flights.length > 0) {
      this.loaded = true;
    }
    this.getAllFlights();
  },
  data() {
    return {
      editibleFlight: null,
      searchInput: '',
      searchQuery: '',
      loaded: false,
      filter: 'all',
      sort: 'sdtDate',
      direction: 'ascending',
      displayFlights: [],
      flightPartitions: { all: [], departure: [], arrival: [] },
      sortMetaMap: {},
      sortCache: {},
      airlineLogosReady: false,
    };
  },
  methods: {
    rebuildFlightIndex() {
      this.flightPartitions = partitionFlightsByType(this.flights);
      this.sortMetaMap = buildSortMetaMap(this.flights);
      this.warmSortCache();
    },
    warmSortCache() {
      ['all', 'departure', 'arrival'].forEach((tab) => {
        this.getSortedPartition(tab, this.sort, this.direction);
      });
    },
    getSortedPartition(filter, sort, direction) {
      const cacheKey = `${filter}|${sort}|${direction}`;
      if (this.sortCache[cacheKey]) {
        return this.sortCache[cacheKey];
      }
      const partition = getPartitionList(this.flightPartitions, filter);
      let sorted = sortFlightsFast(partition, sort, this.sortMetaMap);
      if (direction !== 'ascending') {
        sorted = sorted.slice().reverse();
      }
      this.$set(this.sortCache, cacheKey, sorted);
      return sorted;
    },
    updateDisplayFlights() {
      const sorted = this.getSortedPartition(this.filter, this.sort, this.direction);
      this.displayFlights = this.searchQuery
        ? searchFlights(sorted, this.searchQuery)
        : sorted;
    },
    toggleEditActiveFlyout(flight) {
      this.editibleFlight = flight;
    },
    updatefilter(value) {
      if (this.filter === value) return;
      this.filter = value;
    },
    updateSort(value) {
      if (this.sort === value) {
        this.direction = this.direction === 'ascending' ? 'descending' : 'ascending';
      } else {
        this.sort = value;
        this.direction = 'ascending';
      }
    },
    getSampleFlights() {
      this.$store.commit('activeFlights/flights', flightsSampleData);
      this.loaded = true;
    },
    refreshPortalPage() {
      this.loaded = false;
      this.getAllFlights();
      this.getAllAirportSettings();
    },
    getAllFlights() {
      new ActiveFlights().flights()
        .then((res) => {
          this.$store.commit('activeFlights/flights', res.data.flights);
          this.sortCache = {};
          this.rebuildFlightIndex();
          this.updateDisplayFlights();
        })
        .catch((err) => {
          console.log(err);
        })
        .then(() => {
          this.loaded = true;
        });
    },
    getAllAirportSettings() {
      const gates = new AirportSettings().gates();
      const claims = new AirportSettings().claims();
      const statuses = new AirportSettings().statuses();
      Promise.all([gates, claims, statuses]).then((res) => {
        this.$store.commit('airportSettings/gates', res[0].data);
        this.$store.commit('airportSettings/claims', res[1].data);
        this.$store.commit('airportSettings/statuses', res[2].data);
      })
        .catch((err) => {
          console.log(err);
        });
    }
  }
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">

.flight-manager.portal-page-fill {
  .list-view.table-card {
    padding: 10px 12px 0 !important;
  }

  .list-view {
    width: 100%;
    height: 100%;
    min-height: 0;
    flex-grow: 1;
    position: relative;
    overflow: hidden;
    padding: 0;
    display: flex;
    flex-direction: column;
    -webkit-overflow-scrolling: touch;

    .table-scroll {
      width: 100%;
      height: 100%;
      min-height: 0;
      flex-grow: 1;
      overflow: auto;
      -webkit-overflow-scrolling: touch;
      display: flex;
      flex-direction: column;
    }

    ::v-deep .row-flight-header {
      flex-shrink: 0;
      min-width: 1180px;
      position: sticky;
      top: 0;
      z-index: 2;
    }

    ::v-deep .row-flight,
    ::v-deep .skeleton-row {
      flex-shrink: 0;
      min-width: 1180px;
    }

    .row-container {
      width: 100%;
      min-width: 1180px;
      flex: 1 1 auto;
      position: relative;
      float: left;

      flex-grow: 1;
      overflow-y: visible;

      ::v-deep .row-flight:nth-child(even) {
        background-color: hsl(211, 55%, 98%);
      }

      ::v-deep .row-flight:hover {
        background-color: hsl(211, 70%, 94%) !important;
      }

      .no-data-message {
        width: 100%;
        height: 100%;
        min-height: 120px;
        display: flex;
        align-items: center;
        justify-content: center;
        text-align: center;
        font-size: 16px;
        flex-direction: column;
        color: hsl(211, 25%, 45%);

        .message {
          margin-bottom: 20px;
          padding: 16px 24px;
          border-radius: 8px;
          background: hsl(211, 60%, 97%);
          border: 1px dashed rgba($brand-primary, 0.35);
        }
      }
    }
  }

  .footer.portal-footer {
    .results-icon {
      font-size: 18px;
      color: $brand-primary;
    }
  }

  @media screen and (max-width: 640px) {
    .list-view {
      padding: 8px 8px 0 !important;

      ::v-deep .row-flight-header,
      .row-container {
        min-width: 1040px;
      }
    }

    .footer .results {
      padding-right: 0;
      font-size: 11px;
    }
  }
}

</style>
